import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'

import { Button, BUTTON_THEME, Icon } from '@st-operations/uikit/src/v2/index.js'
import { Dialog3 as Dialog } from '@st-operations/uikit/src/v1/index.js'

import { FIELDS, checkValue } from '../../../lib/data-feeds-auto-fill/index.js'

import './styles.scss'
import { logErrorAsync } from '../../../lib/utility.js'

const lazyLoadXLSX = () => import('xlsx')

const SUPPORTED_EXTENSIONS = ['csv']

const ImportAudiencesModal = ({ onDrop, isOpen, onClose, onImport }) => {
  const [dragging, setDragging] = useState(false)
  const [dragCounter, setDragCounter] = useState(0)
  const [file, setFile] = useState(null)

  const dropRef = useRef(null)

  const handleDrag = (event) => {
    event.preventDefault()
    event.stopPropagation()
  }

  const handleDragIn = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragCounter((prev) => prev + 1)
    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      setDragging(true)
    }
  }

  const handleDragOut = () => {
    setDragCounter((prev) => prev - 1)
  }

  useEffect(() => {
    if (dragCounter === 0) {
      setDragging(false)
    }
  }, [dragCounter])

  const handleAddFile = (file) => {
    if (!SUPPORTED_EXTENSIONS.includes(file.name.split('.').splice(-1)[0])) {
      toast.error('The extension you tried to upload is not supported')
      return
    }

    setFile(file)
    const reader = new FileReader()

    reader.onload = async function (e) {
      const XLSX = await lazyLoadXLSX()

      let data = e.target.result

      try {
        let readData = XLSX.read(data, { type: 'binary' })

        readData.SheetNames.forEach((name) => {
          const worksheet = readData.Sheets[name]

          /* Convert array to json*/
          const dataParse = XLSX.utils
            .sheet_to_json(worksheet, { header: 'A' })
            .map((rowObj) => {
              const rowArray = []
              Object.keys(rowObj)
                .sort()
                .map((column) => {
                  rowArray.push(rowObj[column])
                })
              return rowArray
            })
            .flat()
            // Turn values into pairs of "Value : header"
            .reduce((acc, cur) => {
              if (checkValue(cur) || cur.toLowerCase() === 'x') {
                return [...acc, cur]
              }

              if (acc.length === 0) {
                return acc
              }

              const prevValue = acc.slice(-1)[0]
              if (checkValue(prevValue) || prevValue.toLowerCase() === 'x') {
                return [...acc, cur]
              }

              return acc
            }, [])
            // Separate values into pairs
            .reduce((result, value, index, array) => {
              if (index % 2 === 0) {
                result.push(array.slice(index, index + 2))
              }
              return result
            }, [])

          dataParse.forEach(([value, label]) => {
            if (FIELDS[label]) {
              FIELDS[label].handleChange(onDrop, value, dataParse)
            }
          })
        })

        toast('Successfully updated fields')
      } catch (e) {
        logErrorAsync(e)
        toast.error('There was an error auto filling data')
      }
    }

    reader.readAsBinaryString(file)
  }

  const handleDrop = (event) => {
    event.preventDefault()
    event.stopPropagation()
    setDragging(false)

    if (event.dataTransfer.items && event.dataTransfer.items.length > 0) {
      const file = event.dataTransfer.files[0]
      handleAddFile(file)
      event.dataTransfer.clearData()
      setDragCounter(0)
    }
  }

  const handleRemoveFile = () => setFile(null)

  const handleFileChoose = (event) => {
    handleAddFile(event.target.files[0])
  }

  return (
    <Dialog open={isOpen} close={onClose}>
      <div className="import-audiences-modal">
        <header className="import-audiences-modal__header">
          <Button
            arial-label="Close modal"
            onClick={onClose}
            className="import-audiences-modal__close"
            style={{ margin: '0 0 0 97%', padding: '0' }}
            theme={BUTTON_THEME.GHOST}
          >
            <Icon color="#000" height="16px" icon="delete" type="fill" width="16px" />
          </Button>
          <h1 className="import-audiences-modal__title import-audiences-modal__bold">
            Import Audiences
          </h1>
          <h2 className="import-audiences-modal__subtitle">Batch generate New Records.</h2>
        </header>
        <Choose>
          <When condition={file}>
            <div className="import-audiences-modal__file-container import-audiences-modal__container">
              <Icon
                className="import-audiences-modal__imported-file-icon"
                color="#13682B"
                icon="filecsv"
                margin="auto"
                padding="auto"
                type="fill"
                width="42px"
                height="52px"
              />
              <p className="import-audiences-modal__file-title">{file.name}</p>
              <p className="import-audiences-modal__file-size">{file.size / 100} KB</p>
              <Button
                arial-label="Remove file"
                onClick={handleRemoveFile}
                className="import-audiences-modal__delete"
                theme={BUTTON_THEME.GHOST}
                style={{ margin: '0', padding: '0' }}
              >
                <Icon
                  icon="delete"
                  type="fill"
                  color="#3d3d3d"
                  width="16px"
                  height="16px"
                  margin="0"
                  paddingTop="3px"
                />
              </Button>
            </div>
          </When>
          <Otherwise>
            <label
              onDragEnter={handleDragIn}
              onDragLeave={handleDragOut}
              onDragOver={handleDrag}
              onDrop={handleDrop}
              className={`import-audiences-modal__label ${
                dragging ? 'active' : ''
              } import-audiences-modal__container`}
              ref={dropRef}
            >
              <Icon
                icon="download"
                type="fill"
                color="#13682B"
                width="64px"
                height="64px"
                className="import-audiences-modal__choose-file-icon"
              />
              <p className="import-audiences-modal__text">
                <b className="import-audiences-modal__bold">Choose a file</b> or drag it here
              </p>
              <p className="import-audiences-modal__description">
                Make sure your file is in .CSV format
              </p>
              <input accept=".csv" onChange={handleFileChoose} type="file" />
            </label>
          </Otherwise>
        </Choose>
        <Button
          disabled={!file}
          onClick={onImport}
          theme={BUTTON_THEME.SECONDARY}
          style={{
            margin: '48px 156px 0',
            padding: '8px 60px',
          }}
        >
          Import
        </Button>
      </div>
    </Dialog>
  )
}

ImportAudiencesModal.propTypes = {
  onDrop: PropTypes.func,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  onImport: PropTypes.func,
}

ImportAudiencesModal.defaultProps = {
  onDrop: () => {},
  isOpen: false,
  onClose: () => {},
  onImport: () => {},
}

export default React.memo(ImportAudiencesModal)
